<template>
  <!-- On stage only while it is doing something — pressing, typing, picking from a menu, walking to a
       tab — and fading away as soon as a step is there to be read. Drawn as a coloured arrowhead with
       no tail, the way a collaborator's cursor is drawn in a shared design tool, rather than as the
       system's arrow: next to the reader's own cursor it has to be unmistakably somebody else's. -->
  <div v-if="pointer" class="vhelp__pointer" :class="{ 'is-away': !pointerVisible }" :style="positionStyle">
    <!-- What a drag carries: the picked-up element's own box, in frame units, so it rides the camera at
         the element's real size — under the arrow, which keeps its own size. -->
    <div v-if="pointerCarry" class="vhelp__pointer-carry" :style="carryStyle" />
    <div class="vhelp__pointer-glyph" :style="counterScale">
      <span
        :key="pointerPress"
        class="vhelp__pointer-ring"
        :class="{
          'is-pressed': pointerPress > 0 && !pointerHolding,
          'is-right': pointerPressButton === 'right',
          'is-holding': pointerHolding,
        }"
      />
      <!-- A right-click, said plainly: a mouse with its right button lit, beside the arrow. -->
      <svg
        v-if="pointerPress > 0 && pointerPressButton === 'right'"
        :key="`mouse-${pointerPress}`"
        class="vhelp__pointer-mouse"
        width="30"
        height="42"
        viewBox="0 0 20 28"
        aria-hidden="true"
      >
        <rect x="1.5" y="1.5" width="17" height="25" rx="8.5" fill="#ffffff" stroke="currentColor" stroke-width="1.8" />
        <path d="M10 1.5 A8.5 8.5 0 0 1 18.5 10 L18.5 11.5 L10 11.5 Z" class="vhelp__pointer-mouse-right" />
        <line x1="10" y1="1.5" x2="10" y2="11.5" stroke="currentColor" stroke-width="1.4" />
        <line x1="1.5" y1="11.5" x2="18.5" y2="11.5" stroke="currentColor" stroke-width="1.4" />
      </svg>
      <!-- The key held for this press, as a keycap under the arrow: Strg to add one cell, Umschalt to
           take a run. It goes down before the pointer arrives and comes up when the pointer leaves, so
           it is read while the pointer travels, not only for the length of one press. -->
      <kbd v-if="pointerKey" :key="`key-${pointerKey}`" class="vhelp__pointer-key">
        {{ $t(`help.key.${pointerKey}`) }}
      </kbd>
      <svg class="vhelp__pointer-arrow" width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M3.5 2.8 L20.6 10.2 C21.4 10.55 21.3 11.7 20.45 11.9 L13.4 13.4 L11.6 20.4 C11.4 21.25 10.25 21.3 9.95 20.5 Z"
          fill="currentColor"
          stroke="#ffffff"
          stroke-width="1.8"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * The walkthrough's demo pointer, riding the stage's upper camera layer — see `LewViewHelpStage`.
 * Where it is and whether it shows is the player's (`composables/viewHelp/pointer.ts`); this only
 * draws it.
 */
import { computed } from "vue"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { px } from "@/services/viewHelp/camera"

defineProps<{
  /** Undoes the camera's zoom, so the pointer stays its own size however close the camera is. */
  counterScale: Record<string, string>
}>()

const { pointer, pointerPress, pointerPressButton, pointerHolding, pointerKey, pointerCarry, pointerVisible } =
  useViewHelp()

/** The carried box, placed where it was grabbed relative to the pointer. */
const carryStyle = computed(() => {
  const carry = pointerCarry.value
  if (!carry) return {}
  return {
    width: px(carry.width),
    height: px(carry.height),
    transform: `translate(${px(carry.dx)}, ${px(carry.dy)})`,
  }
})

/** Position tracks the frame; no CSS transition on it, because the motion is already sampled. */
const positionStyle = computed(() => {
  if (!pointer.value) return {}
  return { transform: `translate(${px(pointer.value.x)}, ${px(pointer.value.y)})` }
})
</script>

<style scoped>
/*
 * No CSS transition on its position: the motion is sampled frame by frame in
 * `services/viewHelp/pointer.ts`, and a transition on top would smooth the curve back into a straight
 * slide — losing the bow, the acceleration and the overshoot that make it read as a hand.
 */
.vhelp__pointer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  pointer-events: none;
  transition: opacity 0.22s ease;
}

.vhelp__pointer.is-away {
  opacity: 0;
}

.vhelp__pointer-glyph {
  transform-origin: 0 0;
  color: rgb(var(--v-theme-primary));
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.35));
}

/* The arrowhead's tip is at (3.5, 2.8) of its 24-unit box; this puts the tip, not the box's corner,
   on the point the pointer is aiming at. */
.vhelp__pointer-arrow {
  display: block;
  margin: -3px 0 0 -3.8px;
}

.vhelp__pointer-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 34px;
  height: 34px;
  margin: -17px 0 0 -17px;
  border: 3px solid rgb(var(--v-theme-primary));
  border-radius: 50%;
  opacity: 0;
}

/* Keyed on the press counter, so re-adding the element restarts the animation. */
.vhelp__pointer-ring.is-pressed {
  animation: vhelp-press 420ms ease-out;
}

@keyframes vhelp-press {
  0% {
    opacity: 0.9;
    transform: scale(0.35);
  }
  100% {
    opacity: 0;
    transform: scale(1.9);
  }
}

/* The right button: the ring in the second colour, pulsing out again and again for as long as the
   button is held and the menu opens (RIGHT_HOLD_MS + RIGHT_SHOW_MS), thicker than a left press. */
.vhelp__pointer-ring.is-pressed.is-right {
  border-width: 4px;
  border-color: rgb(var(--v-theme-accent));
  background: rgba(var(--v-theme-accent), 0.14);
  animation: vhelp-press-right 480ms ease-out 3;
}

@keyframes vhelp-press-right {
  0% {
    opacity: 0.95;
    transform: scale(0.4);
  }
  100% {
    opacity: 0;
    transform: scale(2.1);
  }
}

/* Held down across a drag: the ring stays, smaller, so the button reads as still pressed. */
.vhelp__pointer-ring.is-holding {
  opacity: 0.9;
  transform: scale(0.62);
  background: rgba(var(--v-theme-primary), 0.18);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

/* In the pointer's own colour rather than as a grey key on white: over a grid of pale cells a
   neutral keycap was the least visible thing on screen, and it is the whole point of the press. */
.vhelp__pointer-key {
  position: absolute;
  top: 22px;
  left: 18px;
  padding: 3px 10px 4px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-bottom-width: 3px;
  border-radius: 6px;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  animation: vhelp-key 160ms ease-out;
}

@keyframes vhelp-key {
  0% {
    opacity: 0;
    transform: translateY(-4px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vhelp__pointer-key {
    animation: none;
  }
}

.vhelp__pointer-mouse {
  position: absolute;
  top: 16px;
  left: 16px;
  color: rgb(var(--v-theme-accent));
  animation: vhelp-mouse 2000ms ease-out forwards;
}

/* The lit button blinks while the badge is up, so the eye is drawn to which side was pressed. */
.vhelp__pointer-mouse-right {
  fill: rgb(var(--v-theme-accent));
  animation: vhelp-mouse-button 360ms ease-in-out infinite alternate;
}

@keyframes vhelp-mouse {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  8% {
    opacity: 1;
    transform: scale(1.12);
  }
  16% {
    transform: scale(1);
  }
  85% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.9);
  }
}

@keyframes vhelp-mouse-button {
  from {
    fill-opacity: 1;
  }
  to {
    fill-opacity: 0.25;
  }
}

/* The carried element: a lifted copy of its box — tinted, outlined, shadowed, tilted a little. */
.vhelp__pointer-carry {
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 6px;
  background: rgba(var(--v-theme-primary), 0.16);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28);
  transform-origin: 0 0;
  rotate: -1.5deg;
  animation: vhelp-lift 180ms ease-out;
}

@keyframes vhelp-lift {
  from {
    opacity: 0;
    scale: 0.96;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vhelp__pointer-ring.is-pressed,
  .vhelp__pointer-ring.is-pressed.is-right,
  .vhelp__pointer-mouse,
  .vhelp__pointer-mouse-right,
  .vhelp__pointer-carry {
    animation: none;
  }
}
</style>
